import { useContext, useEffect, useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightsClassTypes, isRewardFareClass, maxFlightChildAge, maxFlightInfantAge, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { getEffectiveFlightPrice, getFlightPriceKey, isFnfPriceActive } from 'lib/flights/flightUtils'
import useDefaultAirport from './useDefaultAirport'
import { shallowEqual, useAppDispatch, useAppSelector } from './reduxHooks'
import { fetchFlightPrice } from 'actions/FlightActions'
import { EmptyObject } from 'lib/object/objectUtils'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { EmptyArray } from 'lib/array/arrayUtils'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import useCharterFlightJourneysForHotelDates from './CharterFlights/useCharterFlightJourneysForHotelDates'
import useCharterRewardAvailability from './Flights/useCharterRewardAvailability'
import { getFlightPricePerPerson } from 'lib/offer/flightPriceUtils'
import { hasFeederFlightPriced as hasFeederFlightPricedHelper } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { useRewardSeatsEnabled } from './Flights/useRewardSeatsEnabled'

interface FlightPriceOptions {
  disabled?: boolean
}

function useFlightPrice(
  destinationAirportCode: string | undefined,
  rooms: Array<App.Occupants> = EmptyArray,
  startDate?: string | moment.Moment,
  endDate?: string | moment.Moment,
  originAirportCode?: string,
  nights?: number,
  options: FlightPriceOptions = EmptyObject,
): [number, boolean] {
  const defaultAirport = useDefaultAirport()
  const origin = originAirportCode ?? defaultAirport?.code
  const dispatch = useAppDispatch()
  const startDateParam = moment.isMoment(startDate) ? startDate.format(ISO_DATE_FORMAT) : startDate
  const endDateParam = moment.isMoment(endDate) ? endDate.format(ISO_DATE_FORMAT) : endDate
  const flightRoom = useMemo(() => countOccupantsForFlights(rooms), [rooms])
  const filterKey = useMemo(() => getFlightPriceKey({
    startDate: startDateParam ?? '',
    endDate: endDateParam ?? '',
    origin: origin ?? '',
    destination: destinationAirportCode ?? '',
    numberOfAdults: flightRoom.adults,
    numberOfChildren: flightRoom.children ?? 0,
    numberOfInfants: flightRoom.infants ?? 0,
    nights: nights ?? 0,
  }), [startDateParam, endDateParam, origin, destinationAirportCode, flightRoom, nights])
  const isHookEnabled = !options.disabled

  useEffect(() => {
    if (destinationAirportCode && startDateParam && endDateParam && isHookEnabled) {
      dispatch(fetchFlightPrice(destinationAirportCode, [flightRoom], startDateParam, endDateParam, origin, nights))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, isHookEnabled])

  const flightPrice = useAppSelector(state => state.flights.flightPrices[filterKey])
  const fetching = useAppSelector(state => state.flights.flightPricesLoading[filterKey])

  return [flightPrice, !!fetching]
}

export default useFlightPrice

export function useOfferPageFlightPrice(options: FlightPriceOptions = EmptyObject): {
  totalFlightPrice: number
  loading: boolean
  feederFlightTotalPrice?: number
  loyaltyEarnKey?: string
  rewardAvailable?: boolean
  rewardSeatPointsPerPerson?: number
  rewardSeatCashCoPayPerPerson?: number
  /** Total points across all seat-eligible passengers. */
  rewardSeatPointsTotal?: number
  /** Total flight cash co-pay across all seat-eligible passengers. */
  rewardSeatCashCoPayTotal?: number
  /**
   * True only when the resolved charter journeys carry a positive `feederEstimatedPrice`
   * on at least one leg. Gates feeder-specific UI (e.g. inclusion text rewrite). False
   * while loading, when both legs lack a feeder price for the picked dates, or when the
   * charter path is not active.
   */
  hasFeederFlightPriced: boolean
} {
  const state = useContext(OfferPageStateContext)
  const isHookEnabled = !options.disabled

  const {
    offer,
    rooms,
    checkInDate,
    checkOutDate,
    flightOrigin,
    fareClass,
    flightsEnabled,
    charterBundleRoute,
    charterBundleEnabled,
  } = useAppSelector(appState => ({
    offer: state.offer as App.HotelOffer,
    rooms: state.rooms ?? EmptyArray,
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    flightOrigin: state.airport,
    fareClass: state.fareClass,
    flightsEnabled: offerFlightsEnabled(state, appState),
    charterBundleRoute: state.charterBundleRoute,
    charterBundleEnabled: state.charterBundleEnabled,
  }), shallowEqual)

  const nights = checkInDate && checkOutDate ? moment(checkOutDate).diff(checkInDate, 'days') : 1

  const effectiveCharterBundleRoute = charterBundleEnabled ? charterBundleRoute : undefined
  const charterFlight = effectiveCharterBundleRoute ? CHARTER_FLIGHT_DETAILS[effectiveCharterBundleRoute] : undefined

  const needsCharterFeederFlight = flightsEnabled && offer.offerFlightsEnabled && charterFlight && flightOrigin && flightOrigin.code !== charterFlight.originAirport.airportCode

  // Economy charter searches now receive a `feederEstimatedPrice` per fare from svc-flights
  // (sourced from CharterFeederPrices FDA). When that path is active we skip the legacy
  // additional /flightprice request that fetches a feeder origin -> charter origin price.
  const shouldUseFeederEstimatedPriceFromCharter = !!(
    needsCharterFeederFlight &&
    toBaseFareClass(fareClass) === FlightsClassTypes.ECONOMY
  )

  const shouldUseNormalFlightPrice = isHookEnabled && flightsEnabled && offer.offerFlightsEnabled && (!charterFlight || (needsCharterFeederFlight && !shouldUseFeederEstimatedPriceFromCharter))

  const shouldUseCharterFlightPrice = isHookEnabled && flightsEnabled && offer.offerFlightsEnabled && charterFlight

  let flightDestination: string | undefined = undefined
  if (flightsEnabled && offer.offerFlightsEnabled) {
    if (effectiveCharterBundleRoute && needsCharterFeederFlight && !shouldUseFeederEstimatedPriceFromCharter) {
      flightDestination = charterFlight.originAirport.airportCode
    } else if (!effectiveCharterBundleRoute) {
      flightDestination = offer.flightDestinationPort
    }
  }

  const [flightPrice, flightPriceLoading] = useFlightPrice(
    flightDestination,
    rooms,
    checkInDate,
    checkOutDate,
    flightOrigin?.code,
    nights,
    { disabled: !isHookEnabled },
  )

  const {
    totalPrice: charterFlightPrice,
    fetching: charterFlightPriceLoading,
    loyaltyEarnKey: charterLoyaltyEarnKey,
    rewardAvailable: charterRewardAvailable,
    rewardSeatPointsPerPerson: charterRewardPoints,
    rewardSeatCashCoPayPerPerson: charterRewardCash,
    rewardSeatPointsTotal: charterRewardPointsTotal,
    rewardSeatCashCoPayTotal: charterRewardCashTotal,
    hasFeederFlightPriced: charterHasFeederFlightPriced,
  } = useCharterFlightPrice({
    charterBundleRoute: effectiveCharterBundleRoute,
    checkInDate,
    checkOutDate,
    occupancies: rooms,
    fareClass,
    disabled: !effectiveCharterBundleRoute || !isHookEnabled,
    feederOriginAirportCode: shouldUseFeederEstimatedPriceFromCharter ? flightOrigin?.code : undefined,
  })

  let totalFlightPrice = 0
  let loading = false

  if (shouldUseCharterFlightPrice) {
    if (charterFlightPrice) {
      totalFlightPrice = charterFlightPrice
    } else {
      // Ensure we don't show flight price if we don't have this yet - otherwise we could end up showing just the feeder flight price as the total
      totalFlightPrice = 0
    }
    loading = loading || charterFlightPriceLoading
  } else if (shouldUseNormalFlightPrice) {
    totalFlightPrice = flightPrice
    loading = flightPriceLoading
  }

  return {
    totalFlightPrice,
    loading,
    // Charter feeder is bundled into `totalFlightPrice` via `feederEstimatedPrice` from svc-flights;
    // we only return a separate feeder total for the legacy non-economy flow.
    feederFlightTotalPrice: needsCharterFeederFlight && !shouldUseFeederEstimatedPriceFromCharter ? flightPrice : undefined,
    loyaltyEarnKey: charterLoyaltyEarnKey,
    rewardAvailable: shouldUseCharterFlightPrice ? charterRewardAvailable : undefined,
    rewardSeatPointsPerPerson: shouldUseCharterFlightPrice ? charterRewardPoints : undefined,
    rewardSeatCashCoPayPerPerson: shouldUseCharterFlightPrice ? charterRewardCash : undefined,
    rewardSeatPointsTotal: shouldUseCharterFlightPrice ? charterRewardPointsTotal : undefined,
    rewardSeatCashCoPayTotal: shouldUseCharterFlightPrice ? charterRewardCashTotal : undefined,
    /**
     * True only when the resolved charter journeys carry a positive `feederEstimatedPrice`
     * on at least one leg. Gates feeder-specific UI (e.g. inclusion text rewrite). False
     * while loading or when both legs lack a feeder price for the picked dates.
     */
    hasFeederFlightPriced: shouldUseCharterFlightPrice ? charterHasFeederFlightPriced : false,
  }
}

export function useOfferPageFlightPricePerPerson(options: FlightPriceOptions = EmptyObject) {
  const state = useContext(OfferPageStateContext)
  const flightPrice = useOfferPageFlightPrice(options)

  return {
    ...flightPrice,
    flightPricePerPerson: getFlightPricePerPerson(flightPrice.totalFlightPrice, state.rooms ?? EmptyArray),
  }
}

export interface CharterFlightPriceResult {
  totalPrice: number
  /** Per-adult cash price across both legs (sourced from `.adult` fields on journey / fare family / cheapest metadata). */
  adultPrice?: number
  fetching: boolean
  loyaltyEarnKey?: string
  /** Per-passenger points across all legs (for `/person` display). */
  rewardSeatPointsPerPerson?: number
  /** Per-passenger cash co-pay across all legs (for `/person` display). */
  rewardSeatCashCoPayPerPerson?: number
  /** Total points across all seat-eligible passengers. */
  rewardSeatPointsTotal?: number
  /** Total flight cash co-pay across all seat-eligible passengers. */
  rewardSeatCashCoPayTotal?: number
  /** True when reward fare class is selected and all legs have inventory. */
  rewardAvailable: boolean
  /**
   * True only when journeys have resolved (not fetching) and at least one leg
   * carries a positive `feederEstimatedPrice`. Mirrors `hasFeederFlightPriced`
   * helper in `charterBundleOfferPageUtils`.
   */
  hasFeederFlightPriced: boolean
}

export function useCharterFlightPrice({
  charterBundleRoute,
  checkInDate,
  checkOutDate,
  occupancies = EmptyArray,
  fareClass,
  disabled,
  feederOriginAirportCode,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FareClassWithReward
  disabled?: boolean
  feederOriginAirportCode?: string
}): CharterFlightPriceResult {
  const baseFareClass = toBaseFareClass(fareClass)
  const { departingJourney, returningJourney, fetching, metadata, departingFareFamily, returningFareFamily } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: baseFareClass,
    disabled,
    feederOriginAirportCode,
  })
  // Charter response carries `feederEstimatedPrice` per leg only when fareClass=economy
  // and a feederOriginAirportCode different from MEL was sent. Sum both legs (round-trip).
  const feederEstimatedTotal = (departingJourney?.feederEstimatedPrice ?? 0) + (returningJourney?.feederEstimatedPrice ?? 0)
  const seatEligiblePassengers = useMemo(
    () => sumUpOccupancies(
      occupancies,
      new Set<App.OccupantType>(['adults', 'children', 'teenagers']),
      { maxInfantAge: maxFlightInfantAge, maxChildAge: maxFlightChildAge },
    ),
    [occupancies],
  )

  const departingEffectivePrice = departingJourney
    ? getEffectiveFlightPrice(departingJourney, seatEligiblePassengers)
    : undefined
  const returningEffectivePrice = returningJourney
    ? getEffectiveFlightPrice(returningJourney, seatEligiblePassengers)
    : undefined
  const hasFnfAwareJourneyPricing = !!(
    departingJourney &&
    returningJourney &&
    departingEffectivePrice &&
    returningEffectivePrice &&
    (isFnfPriceActive(departingJourney, seatEligiblePassengers) || isFnfPriceActive(returningJourney, seatEligiblePassengers))
  )

  // Resolve cash flight price from the best available source.
  // Priority: no-date metadata → FnF-aware journey → fare family → journey → metadata fallback.
  const hasSpecificDates = !!(checkInDate && checkOutDate)
  const { cashTotalPrice, cashAdultPrice, cashLegTotals, loyaltyEarnKey } = (() => {
    const journeyLegs = departingJourney && returningJourney
      ? { departing: departingEffectivePrice?.all.totalFare ?? 0, returning: returningEffectivePrice?.all.totalFare ?? 0 }
      : undefined
    const journeyAdultPrice = departingJourney && returningJourney && departingEffectivePrice?.adult.totalFare != null && returningEffectivePrice?.adult.totalFare != null
      ? departingEffectivePrice.adult.totalFare + returningEffectivePrice.adult.totalFare
      : undefined
    const fareFamilyLegs = departingFareFamily && returningFareFamily
      ? { departing: departingFareFamily.promotion?.all ?? departingFareFamily.cost.all ?? 0, returning: returningFareFamily.promotion?.all ?? returningFareFamily.cost.all ?? 0 }
      : undefined
    const fareFamilyAdultPrice = departingFareFamily && returningFareFamily
      ? (departingFareFamily.promotion?.adult ?? departingFareFamily.cost.adult) + (returningFareFamily.promotion?.adult ?? returningFareFamily.cost.adult)
      : undefined

    if (!hasSpecificDates && metadata?.cheapest?.totalPrice) {
      return { cashTotalPrice: metadata.cheapest.totalPrice, cashAdultPrice: metadata.cheapest.adultPrice, cashLegTotals: undefined, loyaltyEarnKey: undefined }
    }
    // Feeder estimate (economy + non-MEL origin only) is bundled into the cash total on the
    // leg-based branches, matching FLY-5300's relative pricing. The date-less `metadata.cheapest`
    // branches already aggregate from the search response, so feeder is not added there.
    if (journeyLegs && hasFnfAwareJourneyPricing) {
      return { cashTotalPrice: journeyLegs.departing + journeyLegs.returning + feederEstimatedTotal, cashAdultPrice: journeyAdultPrice ?? fareFamilyAdultPrice, cashLegTotals: journeyLegs, loyaltyEarnKey: departingJourney?.loyaltyEarnKey ?? departingFareFamily?.loyaltyEarnKey }
    }
    if (fareFamilyLegs) {
      return { cashTotalPrice: fareFamilyLegs.departing + fareFamilyLegs.returning + feederEstimatedTotal, cashAdultPrice: fareFamilyAdultPrice, cashLegTotals: fareFamilyLegs, loyaltyEarnKey: departingFareFamily?.loyaltyEarnKey }
    }
    if (journeyLegs) {
      return { cashTotalPrice: journeyLegs.departing + journeyLegs.returning + feederEstimatedTotal, cashAdultPrice: journeyAdultPrice, cashLegTotals: journeyLegs, loyaltyEarnKey: departingJourney?.loyaltyEarnKey }
    }
    if (metadata?.cheapest?.totalPrice) {
      return { cashTotalPrice: metadata.cheapest.totalPrice, cashAdultPrice: metadata.cheapest.adultPrice, cashLegTotals: undefined, loyaltyEarnKey: undefined }
    }
    return { cashTotalPrice: 0, cashAdultPrice: undefined, cashLegTotals: undefined, loyaltyEarnKey: undefined }
  })()

  const isRewardSeatsEnabled = useRewardSeatsEnabled()
  const isReward = isRewardSeatsEnabled && isRewardFareClass(fareClass)
  const rewardLegs = useMemo(() => {
    if (!isReward || !departingJourney || !returningJourney || !cashLegTotals) return []
    if (!departingJourney.journeyKey || !returningJourney.journeyKey) return []
    return [
      { journeyKey: departingJourney.journeyKey, legTotal: cashLegTotals.departing },
      { journeyKey: returningJourney.journeyKey, legTotal: cashLegTotals.returning },
    ]
  }, [isReward, departingJourney, returningJourney, cashLegTotals])

  const rewardAvailability = useCharterRewardAvailability({
    legs: rewardLegs,
    passengerCount: seatEligiblePassengers,
    disabled: disabled || !isReward || rewardLegs.length === 0,
  })

  return {
    totalPrice: cashTotalPrice,
    adultPrice: cashAdultPrice,
    fetching: fetching || rewardAvailability.isLoading,
    loyaltyEarnKey,
    rewardSeatPointsPerPerson: rewardAvailability.pointsPerPerson,
    rewardSeatCashCoPayPerPerson: rewardAvailability.cashCoPayPerPerson,
    rewardSeatPointsTotal: rewardAvailability.pointsPerPerson != null ? rewardAvailability.pointsPerPerson * seatEligiblePassengers : undefined,
    rewardSeatCashCoPayTotal: rewardAvailability.cashCoPayPerPerson != null ? rewardAvailability.cashCoPayPerPerson * seatEligiblePassengers : undefined,
    rewardAvailable: rewardAvailability.available,
    hasFeederFlightPriced: !fetching && hasFeederFlightPricedHelper(departingJourney, returningJourney),
  }
}
